import React, { useEffect, useState } from 'react';
import { Checkbox, FormControl, Grid, InputLabel, ListItemText, MenuItem, Select } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import {
  sessionsFromExternalSystem,
  examsForSessionFromExternalSystem,
  getSheetTypesForExamFromExternalSystem,
  examsForSessionFromExternalSystemSioePkz,
  getSheetTypesForExamFromExternalSystemSioePkz,
} from '../../services/exams/sessions/sessions.service';
import {
  Exam,
  SelectedExamSheet,
  ExamSheetType,
  ImportedExamSession,
  ExamFromExternalSystem,
  ExamFromExternalSystemWithExamSheets,
} from '../../models/examSessionDto.types';
import { useHistory } from 'react-router-dom';
import { useStyles as selectSession } from './SelectSession.styles';
import { useStyles } from './AddSession.styles';
import { AxiosError } from 'axios';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { useTranslation } from 'react-i18next';
import { IExternalSystem, ISheetTypesForExamFromExternalSystem } from '../../models/session.types';
import SelectItemsLoader from '../SelectItemsLoader.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';

export interface ISelectSession {
  selectedSystem: IExternalSystem;
  handleLoader: (value: boolean) => void;
  handleClose: () => void;
  handleStageFourAndSave: (
    close: boolean,
    session: ImportedExamSession,
    exams: Exam[],
    sheetTypes: ExamSheetType[],
  ) => void;
}

const areExamsEqual = (exam1: ExamFromExternalSystem, exam2: ExamFromExternalSystem): boolean => {
  return exam1.id === exam2.id && exam1.code === exam2.code && exam1.declarationTypeId === exam2.declarationTypeId;
};

const SelectSession = (props: ISelectSession) => {
  const classes = useStyles();
  const selectSessionClasses = selectSession();

  const history = useHistory();
  const { t } = useTranslation();
  const { openAlert } = useLayoutContext();

  const [sessions, setSessions] = useState<ImportedExamSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ImportedExamSession | undefined>(undefined);
  const [exams, setExams] = useState<ExamFromExternalSystem[]>([]);
  const [selectedExams, setSelectedExams] = useState<ExamFromExternalSystemWithExamSheets[]>([]);
  const [searchVal, setSearchVal] = useState('');

  const selectedSystem = props.selectedSystem.name;

  const isSioePkz = selectedSystem === 'SIOEPKZ';

  const numberOfSelectedExams = (): number => {
    return selectedExams.length;
  };

  const eachSelectedExamHasSelectedSheet = (): boolean => {
    return (
      selectedExams.filter(
        (exam) => exam.examSheets && exam.examSheets.filter((examSheet) => examSheet.isSelected).length === 0,
      ).length === 0
    );
  };

  const saveEnabled = selectedSession && numberOfSelectedExams() > 0 && eachSelectedExamHasSelectedSheet();

  useEffect(() => {
    sessionsFromExternalSystem(selectedSystem)
      .then((result: ImportedExamSession[]) => {
        setSessions(result);
      })
      .catch((error: AxiosError) => {
        history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
      });
  }, []);

  const handleNext = () => {
    const localSelectedExams: Exam[] = [];
    const examSheetTypes: ExamSheetType[] = [];
    selectedExams.forEach((selectedExam) => {
      if (selectedSession) {
        localSelectedExams.push({
          id: selectedExam.id,
          name: selectedExam.name,
          code: selectedExam.code,
          examSessionId: selectedSession.id,
          oneId: '',
          oneExamSessionId: '',
          examSheets: [],
          declarationTypeId: selectedExam.declarationTypeId,
          description: selectedExam.description,
        });
        selectedExam.examSheets &&
          selectedExam.examSheets
            .filter((examSheet) => examSheet.isSelected)
            .forEach((selectedSheet) => {
              examSheetTypes.push({
                id: selectedSheet.id,
                name: selectedSheet.name,
                code: selectedSheet.code,
                examSheetTypeId: selectedSheet.id,
                examId: selectedExam.id,
                isSelected: true,
                customNumber: selectedSheet.customNumber,
              });
            });
      }
    });
    if (selectedSession) {
      props.handleStageFourAndSave(false, selectedSession, localSelectedExams, examSheetTypes);
    }
  };

  const retrieveExamsForExamSession = (idExamSession: number) => {
    props.handleLoader(true);
    if (isSioePkz) {
      examsForSessionFromExternalSystemSioePkz(selectedSystem, idExamSession)
        .then((exams: ExamFromExternalSystem[]) => {
          setExams(exams);
        })
        .catch((error: AxiosError) => {
          history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
        })
        .finally(() => {
          props.handleLoader(false);
        });
    } else {
      examsForSessionFromExternalSystem(selectedSystem, idExamSession)
        .then((exams: ExamFromExternalSystem[]) => {
          setExams(exams);
        })
        .catch((error: AxiosError) => {
          history.push(getErrorPageUrl(error?.code ?? 500, 'admin'));
        })
        .finally(() => {
          props.handleLoader(false);
        });
    }
  };

  const handleSelectSession = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    const foundSession = sessions.find((item) => item.id === e.target.value);
    if (foundSession) {
      setSelectedSession(foundSession);
      retrieveExamsForExamSession(foundSession.id);
    } else {
      setExams([]);
    }
  };

  const handleLoadExamSheets = (selectedExam: ExamFromExternalSystemWithExamSheets) => {
    if (!selectedExam.examSheets) {
      if (isSioePkz) {
        getSheetTypesForExamFromExternalSystemSioePkz(
          selectedSession !== undefined ? selectedSession.id : 0,
          selectedExam.id,
        ).then(
          (sheetTypes: ISheetTypesForExamFromExternalSystem[]) => {
            const sortedSheetTypes = [...sheetTypes];
            sortedSheetTypes.sort((a, b) => {
              return a.customNumber.localeCompare(b.customNumber);
            });

            setSelectedExams((prevState) =>
              prevState.map((item) => {
                if (areExamsEqual(selectedExam, item)) {
                  return {
                    ...item,
                    examSheets: sortedSheetTypes.map((sheetType) => {
                      return { ...sheetType, isSelected: false };
                    }),
                  };
                } else {
                  return item;
                }
              }),
            );
          },
          () => openAlert('error'),
        );
      } else {
        getSheetTypesForExamFromExternalSystem(
          selectedSystem,
          selectedExam.id,
          selectedExam.code,
          selectedSession ? selectedSession.number : 0,
        ).then(
          (sheetTypes: ISheetTypesForExamFromExternalSystem[]) => {
            const sortedSheetTypes = [...sheetTypes];
            sortedSheetTypes.sort((a, b) => {
              return a.code.localeCompare(b.code) || a.name.localeCompare(b.name);
            });

            setSelectedExams((prevState) =>
              prevState.map((item) => {
                if (areExamsEqual(selectedExam, item)) {
                  return {
                    ...item,
                    examSheets: sortedSheetTypes.map((sheetType) => {
                      return { ...sheetType, isSelected: false };
                    }),
                  };
                } else {
                  return item;
                }
              }),
            );
          },
          () => openAlert('error'),
        );
      }
    }
  };

  const toggleSelectExam = (exam: ExamFromExternalSystem, checked: boolean) => {
    if (checked) {
      const newExamItemWithExamSheets: ExamFromExternalSystemWithExamSheets = { ...exam, examSheets: null };
      setSelectedExams((prevState) => [...prevState, newExamItemWithExamSheets]);
      handleLoadExamSheets(newExamItemWithExamSheets);
    } else {
      setSelectedExams((prevState) => prevState.filter((item) => !areExamsEqual(item, exam)));
    }
  };

  const renderSelectedSheets = (selectedIds: number[], exam: ExamFromExternalSystemWithExamSheets) => {
    if (selectedIds.length && exam.examSheets) {
      return (
        <div>
          {exam.examSheets
            .filter((item: SelectedExamSheet) => selectedIds.includes(item.id))
            .map((item: SelectedExamSheet) =>
              isSioePkz ? (
                <div key={`${exam.customNumber}`} className={selectSessionClasses.selectedSheet}>
                  {`${item.customNumber}`}
                </div>
              ) : (
                <div key={`${exam.id}-${item.id}`} className={selectSessionClasses.selectedSheet}>
                  {`${item.code}-${item.name}`}
                </div>
              ),
            )}
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  const handleSheetChange = (examSheetsIds: number[], exam: ExamFromExternalSystemWithExamSheets) => {
    setSelectedExams((prevState) =>
      prevState.map((item) => {
        if (areExamsEqual(exam, item)) {
          return {
            ...item,
            examSheets: !item.examSheets
              ? item.examSheets
              : item.examSheets.map((sheet) => {
                  return { ...sheet, isSelected: examSheetsIds.includes(sheet.id) };
                }),
          };
        } else {
          return item;
        }
      }),
    );
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const filteredNames = exams.filter((exam) => {
    return isSioePkz ? exam.description.includes(searchVal) : exam.name.includes(searchVal);
  });

  return (
    <>
      <ModalWithButtons
        xCloseButton={props.handleClose}
        open={true}
        size="lg"
        title={''}
        confirmButtonLabel="Dalej"
        onConfirm={handleNext}
        confirmButtonDisabled={!saveEnabled}
        onCancel={props.handleClose}
        handleClose={props.handleClose}
        isHideBackdrop={true}
      >
        <Grid container justifyContent="center" alignItems="center">
          <div className={classes.numberCircleOutlineFirst}>
            <div className={classes.numbersInCirclesOutlineFirst}>
              <CheckIcon />
            </div>
          </div>
          <div className={classes.textOutline}>
            Dodaj nową sesję <br />
            egzaminacyjną
          </div>
          <hr className={classes.line}></hr>
          <div className={classes.numberCircleOutlineCheck}>
            <div className={classes.numbersInCirclesOutlineCheck}>
              <CheckIcon />
            </div>
          </div>
          <div className={classes.textOutline}>
            Import danych
            <br />
            sesji egzaminacyjnej
          </div>
          <hr className={classes.line}></hr>
          <div className={classes.numberCircleOutlineCheck}>
            <div className={classes.numbersInCirclesOutlineCheck}>
              <CheckIcon />
            </div>
          </div>
          <div className={classes.textOutline}>
            Wybierz system
            <br />
            źródłowy
          </div>
          <hr className={classes.line}></hr>
          <div className={classes.numberCircleInline}>
            <div className={classes.numbersInCirclesInline}>4</div>
          </div>
          <div className={classes.textInline}>
            Wybierz sesję
            <br />
            egzaminacyjną i arkusz
          </div>
        </Grid>
        <Grid container justifyContent="center">
          <div className={classes.middleText}>Wybierz sesję egzaminacyjną</div>
        </Grid>
        <Grid
          container
          justifyContent="center"
          style={{ marginBottom: selectedSession && selectedSession.id != 0 && exams ? '' : '220px' }}
        >
          <FormControl variant="outlined" className={classes.formInput}>
            <InputLabel id="session-select">Sesja egzaminacyjna</InputLabel>
            <Select
              name={selectedSession?.name ?? ''}
              onChange={handleSelectSession}
              labelId="session-select"
              label="Sesja egzaminacyjna"
              defaultValue={''}
            >
              {!sessions.length && <SelectItemsLoader />}
              {sessions.map((session: ImportedExamSession) => (
                <MenuItem disableRipple className={classes.menuItem} key={session.id} value={session.id}>
                  {session.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {selectedSession && selectedSession.id != 0 && exams && (
          <div style={{ marginBottom: '20px' }}>
            <Grid container justifyContent="center">
              <div className={classes.middleText} style={{ padding: '5px' }}>
                {isSioePkz ? 'Wybierz kwalifikację' : 'Wybierz przedmiot'}
              </div>
            </Grid>
            <Grid justifyContent="center" style={{ textAlign: 'center' }}>
              <TextField
                onChange={handleInput}
                value={searchVal}
                type="text"
                name="name-search"
                id="name-search"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                style={{ width: '80%' }}
              />
            </Grid>
            <Grid xs={12} container justifyContent="center">
              <Grid
                item
                style={{
                  height: '300px',
                  overflow: 'auto',
                  width: '80%',
                  border: 'solid 1px #c7c7c7',
                  borderRadius: '4px',
                  padding: '10px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {filteredNames.map((exam: ExamFromExternalSystem) => {
                    const selectedExam = selectedExams.find((item) => areExamsEqual(item, exam));
                    return (
                      <div key={isSioePkz ? `${exam.id}` : `${exam.id}_${exam.code}_${exam.declarationTypeId}`}>
                        <Checkbox
                          disableRipple
                          value={exam.name}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            toggleSelectExam(exam, event.target.checked)
                          }
                          checked={!!selectedExam}
                          color="primary"
                        />
                        <div className={selectSessionClasses.subjectText}>
                          {isSioePkz ? exam.description : exam.name}
                        </div>
                        {!!selectedExam && (
                          <Grid item xs={12} style={{ padding: '5px 0px 5px 40px' }}>
                            <FormControl variant="outlined" className={selectSessionClasses.sheetsFormInput}>
                              <InputLabel className={selectSessionClasses.sheetInput}>Wybierz arkusz</InputLabel>
                              <Select
                                multiple
                                value={
                                  (selectedExam.examSheets &&
                                    selectedExam.examSheets.filter((item) => item.isSelected).map((item) => item.id)) ||
                                  []
                                }
                                onChange={(
                                  e: React.ChangeEvent<{
                                    name?: string | undefined;
                                    value: unknown;
                                  }>,
                                ) => {
                                  if (
                                    selectedExam.examSheets &&
                                    selectedExam.examSheets.find(
                                      (item) => item.id === (e.target.value as unknown as number[])[0] && item.imported,
                                    )
                                  ) {
                                    return;
                                  } else {
                                    handleSheetChange(e.target.value as number[], selectedExam);
                                  }
                                }}
                                renderValue={(value: unknown) => renderSelectedSheets(value as number[], selectedExam)}
                                MenuProps={{ variant: 'menu' }}
                              >
                                {!selectedExam.examSheets && <SelectItemsLoader />}
                                {selectedExam.examSheets &&
                                  selectedExam.examSheets.map((sheet) => (
                                    <MenuItem disableRipple key={sheet.id} value={sheet.id} disabled={sheet.imported}>
                                      <Checkbox
                                        disableRipple
                                        size="small"
                                        checked={sheet.isSelected && !sheet.imported}
                                        color="primary"
                                        disabled={sheet.imported}
                                      />
                                      <ListItemText
                                        style={{ fontSize: '12px' }}
                                        primary={isSioePkz ? `${sheet.customNumber}` : `${sheet.code}-${sheet.name}`}
                                      />
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </ModalWithButtons>
    </>
  );
};

export default SelectSession;
